import { LANGUAGE_CODES } from '@athena/i18n';
import { InvitationKind, LIMITS, PlanCode, PracticeGenerationProvider, PracticeGenerationType, QuestionType, utf8ByteLength, } from '@athena/shared';
import { z } from 'zod';
import { email } from './email.js';
/**
 * Every id in this schema is a uuid the API issued. Bounding the length is not
 * about format: it stops a client posting a megabyte where an id belongs and
 * making the database do the rejecting.
 */
const id = z.string().min(1).max(64);
/** Half a name. Two of these, both required — see `createStudentInput`. */
export const namePart = z
    .string()
    .trim()
    .min(1)
    .max(60)
    .regex(/^[^\p{Cc}\p{Cf}]+$/u);
/**
 * A birth date, which is a calendar date rather than an instant.
 *
 * Bounded at both ends, and both bounds are about the same thing: a value that
 * is not a birth date at all. The future is refused because it has no age, and
 * 1900 because a mistyped year is the commonest way a date field goes wrong
 * and a student born in 0202 should be told rather than filed.
 */
const birthDate = z.coerce
    .date()
    .refine((value) => value.getTime() <= Date.now(), {
    message: 'A birth date cannot be in the future.',
})
    .refine((value) => value.getUTCFullYear() >= 1900, { message: 'Check the year of birth.' })
    .nullish();
/**
 * The 256 KB cap from V1.md section 8, measured the way the parser measures
 * it, with one shared `utf8ByteLength`, so a document the editor accepts is
 * never one the API then rejects.
 */
const markdownSource = z
    .string()
    .min(1, 'Markdown is empty.')
    .refine((value) => utf8ByteLength(value) <= LIMITS.MAX_MARKDOWN_BYTES, {
    message: `Markdown exceeds the ${LIMITS.MAX_MARKDOWN_BYTES} byte limit.`,
});
/**
 * The language enum, validated against the shipped list rather than a regex.
 *
 * GraphQL has already refused anything that is not `ES` or `EN` by the time a
 * resolver runs, so this is the second of the two checks section 18 asks for
 * and not the only one. It is here anyway because the service is what the
 * tests call, and a service that trusts its caller is a service that is only
 * safe while its one caller stays correct.
 */
export const updatePreferredLanguageInput = z.object({
    language: z.enum(LANGUAGE_CODES),
});
export const createTutorInput = z.object({
    displayName: z.string().trim().min(1).max(120),
});
/**
 * What a tutor says about a student when they add one.
 *
 * The name is two fields because it is two things; the one name every screen
 * shows is derived from them on the way out (`domain/students`), so nothing
 * here produces a `displayName`.
 *
 * `email` is required, and it is not a contact detail — it is what the student
 * signs in with, and `acceptInvitation` refuses to hand the row to anybody
 * else. `birthDate` is the only optional field: `Student.age` is derived from
 * it, and a tutor who does not know it leaves it out.
 *
 * There is no school year. It was free text a tutor typed — "Year 5", "5th
 * grade", "Quinto" — that nothing in the product read, and a birth date
 * answers the question it stood in for.
 */
export const createStudentInput = z.object({
    firstName: namePart,
    lastName: namePart,
    email,
    birthDate,
});
export const createInvitationInput = z.object({
    studentId: id,
    kind: z.enum(InvitationKind),
});
/**
 * A magic-link-sized opaque token. The lower bound is the point: an eight
 * character "token" is a guessing attempt, not a typo.
 */
export const acceptInvitationInput = z.object({
    token: z.string().trim().min(16).max(512).nullish(),
});
/**
 * `subjectId` is an id, never a name. The catalog is system-managed, so the
 * only legitimate value is one the API handed out, and the server still checks
 * it exists and is active before writing it.
 */
export const createPracticeInput = z.object({
    markdown: markdownSource,
    subjectId: id,
});
export const updatePracticeDraftInput = z.object({
    practiceId: id,
    markdown: markdownSource,
});
const authoredText = z.string().trim().min(1).max(LIMITS.MAX_MARKDOWN_BYTES);
const authoredOptionInput = z.object({
    id,
    text: authoredText,
    isCorrect: z.boolean(),
});
const authoredPairInput = z.object({
    id,
    leftText: authoredText,
    rightText: authoredText,
});
/** A full replacement of one question. Its stored type decides which lists apply. */
export const updateQuestionInput = z.object({
    questionId: id,
    prompt: authoredText,
    explanation: z.string().trim().max(LIMITS.MAX_MARKDOWN_BYTES).nullish(),
    options: z.array(authoredOptionInput).max(LIMITS.MAX_OPTIONS),
    acceptedAnswers: z.array(authoredText).max(LIMITS.MAX_OPTIONS),
    correctOrder: z.array(id).max(LIMITS.MAX_OPTIONS),
    pairs: z.array(authoredPairInput).max(LIMITS.MAX_PAIRS),
});
export const deleteQuestionInput = z.object({ questionId: id });
export const createPracticeVersionInput = z.object({ practiceId: id });
/**
 * A tutor's photographed pages, on their way to be transcribed.
 *
 * Base64 rather than a multipart upload: a compressed page is a few hundred
 * kilobytes and Gemini takes base64 inline anyway, so a file-upload transport
 * would be a second request format and a new dependency to arrive at the same
 * bytes.
 *
 * Every page in one request, in the order the tutor chose them. Order is not
 * decoration: it is the only thing that says which transcription is which
 * page, and a worksheet read back to front is a practice whose questions are
 * shuffled.
 *
 * The character ceiling is a boundary check, not the real limit. It stops an
 * absurd payload before it is decoded at all; the service applies the limits
 * that matter, per image and per request, in decoded bytes.
 */
const BASE64_MAX_LENGTH = 8_000_000;
export const extractableImageType = z.enum(['image/png', 'image/jpeg', 'image/webp']);
const extractionImage = z.object({
    /**
     * Standard base64, no `data:` prefix and no whitespace. Checked rather than
     * trusted: this string is about to be handed to an external service, and
     * "looks like base64" is the cheapest thing to be sure of first.
     */
    data: z
        .string()
        .min(1)
        .max(BASE64_MAX_LENGTH)
        .regex(/^[A-Za-z0-9+/]+={0,2}$/, 'Expected base64 image data.'),
    mimeType: extractableImageType,
});
/**
 * One uploaded PDF.
 *
 * The name travels with the bytes because a tutor who uploaded four documents
 * and got one refusal needs to be told which one. It is the tutor's own file
 * name and is never trusted for anything: nothing opens it as a path, nothing
 * writes it to disk, and the only thing it is used for is being quoted back
 * into an error message.
 */
const extractionPdf = z.object({
    /** Bounded so an absurd name cannot be reflected into an error or a log. */
    name: z.string().min(1).max(255),
    data: z
        .string()
        .min(1)
        .max(BASE64_MAX_LENGTH)
        .regex(/^[A-Za-z0-9+/]+={0,2}$/, 'Expected base64 PDF data.'),
});
/**
 * A tutor's source material: photographed pages, or PDFs. Never both.
 *
 * Both lists are optional and exactly one must be non-empty, and that rule is
 * *not* expressed here. It lives in the extraction service, deliberately: a
 * Zod refinement would throw a `BAD_USER_INPUT` before any resolver ran, and
 * the tutor would get an unlocalized transport error for a condition the
 * product has a sentence about. The service refuses it as a `userError` with
 * a code the client words itself, which is the same treatment every other
 * refusal in this flow gets.
 *
 * What is bounded here is the shape and the counts. The per-document and
 * per-request byte ceilings need decoded lengths, which a schema cannot see.
 */
export const extractPagesInput = z.object({
    /**
     * No more than the uploader offers. Bounded here rather than trusted from
     * the client: the browser refuses an eleventh image, and the browser is not
     * what makes the rule hold.
     *
     * Optional rather than required, because a PDF request carries none. `min(1)`
     * is gone for the same reason — "at least one source" is now a statement
     * about the pair of lists, so the service makes it.
     */
    images: z.array(extractionImage).max(LIMITS.MAX_EXTRACTION_IMAGES).optional(),
    /** The other source type. Fewer allowed than images: a PDF is a document. */
    pdfs: z.array(extractionPdf).max(LIMITS.MAX_EXTRACTION_PDFS).optional(),
});
/**
 * What a tutor asks a model to write a practice from.
 *
 * The source material is the combined Markdown the extraction step read off
 * their images, so it is bounded the same way an authored document is: one cap
 * on Markdown, measured in UTF-8 bytes, wherever Markdown enters the API. A
 * tutor who photographed more pages than the parser would ever accept should
 * be told before a provider is paid to read them.
 *
 * `questionCount` is optional, because the tutor has already said the two
 * things that matter — the subject, and the pages. It is deliberately *not*
 * defaulted here: the default is a generation rule and lives with the other
 * generation rules, so that "omitted becomes 30" is one testable function
 * rather than a Zod argument nothing calls directly.
 *
 * There is no topic and no free-text instruction: both were optional fields a
 * tutor almost always left blank, and the topic inferred from the source
 * material is the topic of the source material. There is no education level.
 * The provider field is the narrow development-only exception: schema-valid
 * here, then authorized against the server capability in the generation service.
 */
export const generatePracticeInput = z.object({
    extractionId: id.nullish(),
    subjectId: id,
    sourceMarkdown: markdownSource,
    /** Which question set the automatic generator should write. */
    practiceType: z.enum(PracticeGenerationType).nullish(),
    questionTypes: z
        .array(z.enum(QuestionType))
        .min(1)
        .max(Object.keys(QuestionType).length)
        .refine((types) => new Set(types).size === types.length, 'Question types must be unique.')
        .nullish(),
    /** Development-only provider override; authorization is enforced by the API service. */
    provider: z.enum(PracticeGenerationProvider).nullish(),
    /**
     * Bounded here as well as defaulted later, so a client asking for a thousand
     * questions is refused before a provider is called rather than after.
     *
     * The plan's own ceiling is applied on top of this and is *not* expressible
     * here: what a tutor may ask for depends on their subscription, which a
     * schema cannot see. A Free tutor asking for a hundred gets fifteen, because
     * the generation service clamps to the plan before a provider is called.
     */
    questionCount: z.number().int().min(1).max(LIMITS.MAX_QUESTIONS).nullish(),
    /**
     * The client's key for one logical generation, so a retry is free.
     *
     * Generation costs an AI credit, and the credit is taken before the model is
     * called. Without a key, a request that timed out on the wire and was sent
     * again would be two credits for one practice. With one, the second request
     * finds the first's ledger row and charges nothing.
     *
     * Optional, and the server invents one when it is absent — a client that
     * does not ask for retry safety should still not be able to collide with
     * another request. Opaque to the server: it is compared, never parsed, and
     * it is scoped to the tutor by a unique index so one tutor's key cannot
     * reach another's ledger.
     */
    idempotencyKey: z.string().trim().min(8).max(128).nullish(),
});
export const publishPracticeInput = z.object({
    practiceId: id,
});
export const assignPracticeInput = z.object({
    practiceId: id,
    studentId: id,
});
export const unassignPracticeInput = z.object({
    practiceId: id,
    studentId: id,
});
export const archivePracticeInput = z.object({
    practiceId: id,
});
export const restorePracticeInput = z.object({
    practiceId: id,
});
/**
 * Whether an instant sits exactly on an hour or a half hour.
 *
 * The end-date control offers 30-minute steps, and this is the rule behind it
 * rather than a restatement of it: the browser is untrusted, so a value that
 * did not come from that control is refused here whatever a client believes.
 *
 * Read in UTC, because the server has no idea what timezone a tutor is in and
 * an instant is an instant. Every zone whose offset is a whole hour or a half
 * hour — which is all of them bar a handful at :45 — maps a local :00/:30 onto
 * a UTC :00/:30, so the check means the same thing on both sides of the wire.
 *
 * Seconds and milliseconds have to be zero too. "On the half hour" is a
 * property of the whole instant, and 10:30:07 is not on it.
 */
function onHalfHour(when) {
    const minutes = when.getUTCMinutes();
    return ((minutes === 0 || minutes === 30) &&
        when.getUTCSeconds() === 0 &&
        when.getUTCMilliseconds() === 0);
}
/**
 * Everything the Practice Settings panel holds, in one input.
 *
 * One schema rather than three because the panel is one form with one Save.
 * Every field is sent on every save, changed or not: this replaces the
 * practice's settings rather than patching them, which is what makes "what is
 * on screen when Save is pressed is what the practice ends up with" true
 * without the client having to work out a diff.
 *
 * Null is meaningful in `availableUntil` and `maxAttempts`: it is how a tutor
 * clears an end date or an attempt limit. The lower bound on `maxAttempts` is
 * 1 — zero attempts is not a limit, it is an unassignment, and there is a
 * mutation for that.
 *
 * `durationMinutes` is required when the timer is on. When it is off, a
 * supplied duration is preserved: switching the timer back on should restore
 * the tutor's previous value rather than making them enter it again.
 */
export const updatePracticeSettingsInput = z
    .object({
    practiceId: id,
    subjectId: id,
    availableUntil: z.coerce.date().nullish(),
    maxAttempts: z.number().int().min(1).max(LIMITS.MAX_ATTEMPTS).nullish(),
    timed: z.boolean(),
    durationMinutes: z
        .number()
        .int()
        .min(LIMITS.MIN_PRACTICE_DURATION_MINUTES)
        .max(LIMITS.MAX_PRACTICE_DURATION_MINUTES)
        .nullish(),
})
    .transform((input) => ({
    practiceId: input.practiceId,
    subjectId: input.subjectId,
    availableUntil: input.availableUntil ?? null,
    maxAttempts: input.maxAttempts ?? null,
    timed: input.timed,
    durationMinutes: input.durationMinutes ?? null,
}))
    .refine((input) => !input.timed || input.durationMinutes !== null, {
    message: 'A timed practice needs a duration in minutes.',
    path: ['durationMinutes'],
})
    .refine((input) => input.availableUntil === null || onHalfHour(input.availableUntil), {
    message: 'An end date must fall on the hour or the half hour.',
    path: ['availableUntil'],
});
/**
 * Every field optional, because an empty filter is the default view rather
 * than an error. Dates are coerced so the wire's ISO string becomes a Date
 * before it reaches a `where`.
 */
export const dashboardFilterInput = z.object({
    studentId: id.nullish(),
    practiceId: id.nullish(),
    since: z.coerce.date().nullish(),
    until: z.coerce.date().nullish(),
});
export const startAttemptInput = z.object({
    practiceId: id,
});
/**
 * An ASSOCIATION answer: the option ids in the order the student put them.
 *
 * Capped at the option cap, because that is the longest an order over a
 * question's options can honestly be. Duplicates are malformed independently
 * of the question, so reject them here for both save and submit. Ownership is
 * still checked against the question in the application/repository layer.
 */
const submittedOrder = z
    .array(id)
    .max(LIMITS.MAX_OPTIONS)
    .refine((optionIds) => new Set(optionIds).size === optionIds.length, {
    message: 'An ordering answer cannot contain the same option more than once.',
})
    .nullish();
/**
 * A MATCHING answer: what the student put against each left item.
 *
 * `leftId` is the left item and `rightId` the right one. Capped at the pair
 * cap, because one match per left item is the most a question can hold —
 * anything longer repeats a left item, which the scorer refuses with the
 * question id attached, but a client posting ten thousand of them should be
 * stopped before it reaches a `Map`.
 *
 * Which of the two ids belongs to which question is a rule about a question
 * this package cannot see, so it is the scorer's to enforce and not this
 * schema's.
 */
const matches = z
    .array(z.object({ leftId: id, rightId: id }))
    .max(LIMITS.MAX_PAIRS)
    .nullish();
/**
 * `selectedOptionId`, `textAnswer`, `submittedOrder` and `matches` are all
 * optional here and none is required, because "exactly one of the four,
 * matching the question's type" is a rule about a question this package
 * cannot see. The
 * scorer rejects the whole submission for a wrong-shaped answer (V1.md section
 * 10) and returns the question id with it, which is the error a student can
 * act on.
 */
export const submittedAnswerInput = z.object({
    questionId: id,
    selectedOptionId: id.nullish(),
    textAnswer: z.string().max(2000).nullish(),
    submittedOrder,
    matches,
});
export const submitAttemptInput = z.object({
    attemptId: id,
    /**
     * One answer per question at most, so the question cap is also the answer
     * cap. A longer list is a malformed client, not a large practice.
     */
    answers: z.array(submittedAnswerInput).max(LIMITS.MAX_QUESTIONS),
});
/** Same shape as a submitted answer, minus nothing: what changes at submit is scoring, not the answer. */
export const savedAnswerInput = z.object({
    questionId: id,
    selectedOptionId: id.nullish(),
    textAnswer: z.string().max(2000).nullish(),
    submittedOrder,
    matches,
});
export const saveAttemptInput = z.object({
    attemptId: id,
    answers: z.array(savedAnswerInput).max(LIMITS.MAX_QUESTIONS),
    /** A question index, so it is bounded by the same cap the questions are. */
    currentQuestionPosition: z.number().int().min(0).max(LIMITS.MAX_QUESTIONS).nullish(),
});
// ── Subscriptions ────────────────────────────────────────────────────────
//
// Nothing here carries a price, a capacity, a usage number or a date the
// server is supposed to decide. A client that could send one would be sending
// a suggestion, and the server reads none of them: the plan catalog in
// `@athena/shared` says what a plan allows, and the subscription row says
// which plan somebody is on. What a tutor may send is *which plan they are
// asking about* and *what they want to keep*, and that is the whole of it.
/**
 * A plan, as the enum rather than as a string.
 *
 * The same treatment `InvitationKind` and `Language` get, for the same reason:
 * GraphQL has already refused anything that is not one of the three by the
 * time a resolver runs, and this is the second of the two checks — the one
 * that holds when a service is called directly from a test.
 */
const planCode = z.enum(PlanCode);
/**
 * Something a person typed for somebody else to read: a tutor explaining why
 * they want an upgrade, or an administrator recording why they granted one.
 *
 * Bounded, trimmed, and never rendered as anything but text. The length is a
 * paragraph rather than an essay, because the field is a note beside a
 * decision and not a support thread.
 */
const note = z.string().trim().min(1).max(500);
/**
 * A reason an administrator gives for a privileged change.
 *
 * Required rather than optional wherever it appears, and that is the point of
 * giving it its own name: the audit trail's value is in the "why", and a
 * reason nobody had to type is a column full of empty strings.
 */
const auditReason = z.string().trim().min(1).max(500);
/**
 * A tutor asking to be moved onto a paid plan.
 *
 * Carries no price and no effective date. It is a request, it grants nothing,
 * and an administrator decides — so the only thing the tutor gets to say is
 * which plan, and optionally why.
 */
export const requestPlanUpgradeInput = z.object({
    planCode,
    message: note.nullish(),
});
export const withdrawUpgradeRequestInput = z.object({
    requestId: id,
});
/**
 * A tutor moving to a smaller plan.
 *
 * No effective date: when a downgrade lands is the end of the period that is
 * paid for, which is the server's arithmetic and not a field. A client that
 * could name the date could name today.
 */
export const schedulePlanChangeInput = z.object({
    planCode,
    reason: note.nullish(),
});
/**
 * What the tutor wants to keep when a downgrade lands.
 *
 * Two lists of ids they already own — validated against that in the service,
 * because whether an id is theirs is a database question. Bounded by the
 * largest plan's capacities rather than by the target plan's: the point of the
 * cap here is to refuse an absurd payload, and the real "that is more than the
 * new plan holds" refusal is a sentence the tutor should read rather than a
 * schema error.
 */
const uniqueIdList = (maximum, label) => z
    .array(id)
    .max(maximum)
    .refine((ids) => new Set(ids).size === ids.length, {
    message: `${label} cannot contain duplicate ids.`,
});
export const saveDowngradeSelectionInput = z.object({
    studentIds: uniqueIdList(100, 'Student selection'),
    practiceIds: uniqueIdList(500, 'Practice selection'),
});
/** Suspending or restoring one tutor–student connection. */
export const studentConnectionInput = z.object({
    studentId: id,
});
/** Bringing one subscription-suspended practice back into the active set. */
export const restorePracticeSlotInput = z.object({
    practiceId: id,
});
export const revokeInvitationInput = z.object({
    invitationId: id,
});
// ── Administration ───────────────────────────────────────────────────────
//
// Every one of these requires an admin profile on the server. The schemas
// below check shape; `requireAdmin` checks authority, and neither stands in
// for the other.
/**
 * An administrator putting an account on a plan.
 *
 * `accessEndsAt` is how an admin-managed paid subscription stays honest: the
 * subscription was arranged outside Athena, somebody has to record how far it
 * is paid up to, and past that instant the account is on Free whatever the
 * plan column says. Null means access with no recorded end, which is a
 * deliberate choice an administrator makes rather than a default they fall
 * into.
 *
 * There is no `periodAnchorAt` and no allowance here. Both are the server's:
 * the anchor is kept across a paid-to-paid change so an upgrade does not
 * restart the month, and the allowance comes from the plan catalog.
 */
export const adminAssignPlanInput = z.object({
    tutorId: id,
    planCode,
    accessEndsAt: z.coerce.date().nullish(),
    reason: auditReason,
});
/**
 * An administrator scheduling a change for later.
 *
 * `effectiveAt` is optional: omitted, it falls to the end of the current paid
 * period, which is the same arithmetic a tutor's own downgrade uses. Given, it
 * is honoured — an administrator sorting out a support case sometimes needs a
 * date the product's own rules would not have picked.
 */
export const adminSchedulePlanChangeInput = z.object({
    tutorId: id,
    planCode,
    effectiveAt: z.coerce.date().nullish(),
    reason: auditReason,
});
/** Recording when an account's paid access stops. */
export const adminEndPaidAccessInput = z.object({
    tutorId: id,
    accessEndsAt: z.coerce.date(),
    reason: auditReason,
});
/**
 * An administrator answering an upgrade request.
 *
 * Approving does not, on its own, change a plan: it answers the request, and
 * the service applies the plan in the same transaction so the two cannot
 * disagree. Declining answers it and changes nothing.
 */
export const adminDecideUpgradeRequestInput = z.object({
    requestId: id,
    approve: z.boolean(),
    /**
     * Required for either answer, like every other privileged change's reason
     * (SECURITY_AUDIT.md SEC-016): "no" without a reason is not an answer, and
     * "yes" without one is a paid plan granted with nothing saying why.
     */
    note: auditReason,
});
/**
 * Correcting the AI usage ledger.
 *
 * A signed amount, because a correction goes both ways: negative returns
 * credits a tutor was wrongly charged, positive takes back ones they were
 * wrongly given. Zero is refused — it is not a correction, and it would put a
 * row in the ledger that says nothing.
 *
 * Bounded well below any real allowance so a slipped keystroke cannot hand
 * somebody a thousand generations.
 */
export const adminSetStudentLimitInput = z.object({
    tutorId: id,
    studentLimit: z.number().int().min(1).max(2147483647).nullable(),
    reason: auditReason,
});
export const adminAdjustAiUsageInput = z.object({
    tutorId: id,
    amount: z
        .number()
        .int()
        .min(-100)
        .max(100)
        .refine((value) => value !== 0, { message: 'An adjustment of zero changes nothing.' }),
    reason: auditReason,
});
/**
 * User lookup.
 *
 * A fragment of an address or a name, because an administrator opening a
 * support request has what the person typed rather than their user id. Empty
 * is allowed and means "the most recent accounts", which is the screen's
 * first load.
 */
export const adminUserSearchInput = z.object({
    query: z.string().trim().max(254),
    limit: z.number().int().min(1).max(100).nullish(),
});
//# sourceMappingURL=inputs.js.map